import { motion, HTMLMotionProps, Variants } from 'framer-motion';
import { ReactNode } from 'react';
import { scaleIn, scaleUp } from '@/lib/motion/variants';
import { useReducedMotion } from '@/lib/motion/hooks/use-reduced-motion';

interface ScaleInProps extends Omit<HTMLMotionProps<'div'>, 'children' | 'viewport'> {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
  /** Whether to animate when element enters viewport */
  viewport?: boolean;
  /** Use larger scale animation for emphasis */
  emphasis?: boolean;
}

export function ScaleIn({
  children,
  delay = 0,
  duration,
  className,
  viewport = false,
  emphasis = false,
  ...props
}: ScaleInProps) {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const variants: Variants = emphasis ? scaleUp : scaleIn;

  const animateProps = viewport
    ? {
        initial: 'hidden',
        whileInView: 'visible',
        viewport: { once: true, margin: '-50px' },
      }
    : {
        initial: 'hidden',
        animate: 'visible',
      };

  return (
    <motion.div
      variants={variants}
      {...animateProps}
      exit="exit"
      transition={{ delay, ...(duration && { duration }) }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
